import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    debounce,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import { Organization } from 'utils';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { Commission } from '../../../../networking/networking.types';
import {
    getCommissionsForAdmins,
    getCompPlanSummary,
    getCompPlanRecipients,
    getCompPlanExportCsv,
} from '../commissions.api';
import {
    CompPlanReportParams,
    CompPlanSummary,
    CompPlanRecipient,
    GetCommissionsParams,
} from '../commissions.types';
import StripeLogo from '../../../../../images/stripe-logo.png';

const ResponsiveFormItem = styled(Box)(({ theme }) => ({
    width: 'calc(20% - 16px)',
    maxWidth: '320px',
    margin: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        width: 'calc(100% - 16px)',
        maxWidth: 'none',
    },
}));

const ResponsiveForm = styled('div')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    margin: theme.spacing(-1),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

type PaidFilter = 'paid' | 'unpaid' | 'all';
type DatePreset = 'all' | 'ytd' | 'last_year' | 'trailing_12';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const currencyCompact = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
});
const monthLabelFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
});

/** Amounts come from the API in cents; render in dollars. */
const fromCents = (cents: number | null | undefined): number => (cents ?? 0) / 100;
const formatCents = (cents: number | null | undefined): string => currency.format(fromCents(cents));

/** "2026-06" -> "Jun 2026" */
const formatMonthLabel = (ym: string): string => {
    if (!ym || ym.length < 7) {
        return ym || 'Unknown';
    }
    const date = new Date(`${ym}-01T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? ym : monthLabelFormatter.format(date);
};

const toIsoDate = (value?: string): string => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

/** "YYYY-MM" -> last calendar day, e.g. "2026-02" -> "2026-02-28". */
const lastDayOfMonth = (ym: string): string => {
    const [year, month] = ym.split('-').map(Number);
    const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${ym}-${String(day).padStart(2, '0')}`;
};

/** Shifts a YYYY-MM key by a number of months. */
const shiftMonth = (ym: string, delta: number): string => {
    const [year, month] = ym.split('-').map(Number);
    const base = new Date(Date.UTC(year, month - 1 + delta, 1));
    return `${base.getUTCFullYear()}-${String(base.getUTCMonth() + 1).padStart(2, '0')}`;
};

const SummaryCard = ({
    label,
    value,
    sub,
    color,
}: {
    label: string;
    value: string;
    sub?: string;
    color?: string;
}) => (
    <Card variant="outlined" sx={{ minWidth: 170, flex: '1 1 170px' }}>
        <CardContent>
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color }}>
                {value}
            </Typography>
            {sub && (
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const triggerCsvDownload = (filename: string, lines: string[]) => {
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const CompPlanReport = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

    // Data
    const [summary, setSummary] = useState<CompPlanSummary | null>(null);
    const [recipients, setRecipients] = useState<CompPlanRecipient[]>([]);
    const [commissions, setCommissions] = useState<Commission[]>([]);
    const [totalCommissions, setTotalCommissions] = useState(0);
    const [organizations, setOrganizations] = useState<Organization[]>([]);

    // Loading / UI
    const [loadingReport, setLoadingReport] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
        open: false,
        message: '',
    });

    // Filters
    const [paidFilter, setPaidFilter] = useState<PaidFilter>('paid');
    const [datePreset, setDatePreset] = useState<DatePreset>('all');
    const [startMonth, setStartMonth] = useState<string>('');
    const [endMonth, setEndMonth] = useState<string>('');
    const [recipient, setRecipient] = useState<{ uuid: string; name: string } | null>(null);

    // Drill-down paging / sorting
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 25,
    });
    const [detailSort, setDetailSort] = useState<string>('-earned_date');

    // Bumped by the Refresh button to force a re-fetch with the same filters.
    const [refreshKey, setRefreshKey] = useState(0);

    // Derived backend params
    const fromDate = startMonth ? `${startMonth}-01` : undefined;
    const toDate = endMonth ? lastDayOfMonth(endMonth) : undefined;
    const isPaid = paidFilter === 'paid' ? 1 : paidFilter === 'unpaid' ? 0 : undefined;
    const orgUuid = recipient?.uuid;

    const reportParams: CompPlanReportParams = useMemo(
        () => ({ from: fromDate, to: toDate, is_paid: isPaid, organization_uuid: orgUuid }),
        [fromDate, toDate, isPaid, orgUuid],
    );

    // Load aggregated summary + recipient leaderboard whenever filters change.
    useEffect(() => {
        let cancelled = false;
        setLoadingReport(true);
        Promise.all([getCompPlanSummary(reportParams), getCompPlanRecipients(reportParams)])
            .then(([summaryRes, recipientsRes]) => {
                if (cancelled) {
                    return;
                }
                setSummary(summaryRes.data);
                setRecipients(recipientsRes.data.data);
            })
            .catch((error) => {
                console.error('Failed to load comp plan report', error);
                setSnackbar({ open: true, message: 'Failed to load comp plan report.' });
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingReport(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [reportParams, refreshKey]);

    // Load the server-paginated drill-down detail.
    useEffect(() => {
        let cancelled = false;
        setLoadingDetail(true);
        const params: GetCommissionsParams = {
            earned_date_from: fromDate,
            earned_date_to: toDate,
            is_paid: isPaid,
            'partner.organization': orgUuid,
            sort: detailSort,
            page: paginationModel.page + 1,
            per_page: paginationModel.pageSize,
        };
        getCommissionsForAdmins(params)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                setCommissions(response.data.data);
                setTotalCommissions(response.data.meta?.total ?? 0);
            })
            .catch((error) => {
                console.error('Failed to load commission detail', error);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingDetail(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [reportParams, fromDate, toDate, isPaid, orgUuid, detailSort, paginationModel, refreshKey]);

    useEffect(() => {
        adminGetAllOrganizations('')
            .then((response) => setOrganizations(response.data.data))
            .catch((error) => console.error('Failed to fetch organizations', error));
    }, []);

    const fetchOrganizations = useMemo(
        () =>
            debounce((searchTerm: string) => {
                adminGetAllOrganizations(searchTerm)
                    .then((response) => setOrganizations(response.data.data))
                    .catch((error) => console.error('Failed to fetch organizations', error));
            }, 300),
        [],
    );

    // Reference month keys for preset ranges.
    const { currentMonthKey, ytdStartKey, trailing12StartKey } = useMemo(() => {
        const now = new Date();
        const monthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(
            2,
            '0',
        )}`;
        return {
            currentMonthKey: monthKey,
            ytdStartKey: `${now.getUTCFullYear()}-01`,
            trailing12StartKey: shiftMonth(monthKey, -11),
        };
    }, []);

    const resetToFirstPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));

    const applyPreset = (preset: DatePreset) => {
        setDatePreset(preset);
        resetToFirstPage();
        if (preset === 'all') {
            setStartMonth('');
            setEndMonth('');
        } else if (preset === 'ytd') {
            setStartMonth(ytdStartKey);
            setEndMonth(currentMonthKey);
        } else if (preset === 'last_year') {
            const lastYear = Number(ytdStartKey.slice(0, 4)) - 1;
            setStartMonth(`${lastYear}-01`);
            setEndMonth(`${lastYear}-12`);
        } else if (preset === 'trailing_12') {
            setStartMonth(trailing12StartKey);
            setEndMonth(currentMonthKey);
        }
    };

    const handleExport = useCallback(() => {
        setIsExporting(true);
        getCompPlanExportCsv(reportParams)
            .then((response) => {
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `comp-plan-payouts_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error('Failed to export CSV', error);
                setSnackbar({ open: true, message: 'Failed to export CSV.' });
            })
            .finally(() => setIsExporting(false));
    }, [reportParams]);

    const exportRecipientsCsv = () => {
        const headers = [
            'Recipient',
            'In View',
            'Paid YTD',
            'Paid Trailing 12 Mo',
            'Paid All Time',
            'Payments',
            'Last Paid',
        ];
        const escape = (value: string | number) => {
            const str = String(value ?? '');
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const lines = [headers.join(',')];
        recipients.forEach((row) => {
            lines.push(
                [
                    escape(row.organization_name),
                    fromCents(row.in_view).toFixed(2),
                    fromCents(row.paid_ytd).toFixed(2),
                    fromCents(row.paid_trailing_12).toFixed(2),
                    fromCents(row.paid_all_time).toFixed(2),
                    row.payment_count,
                    row.last_paid ?? '',
                ].join(','),
            );
        });
        triggerCsvDownload(
            `comp-plan-by-recipient_${new Date().toISOString().slice(0, 10)}.csv`,
            lines,
        );
    };

    const handleDetailSort = (model: GridSortModel) => {
        const field = model[0]?.field;
        const order = model[0]?.sort;
        if (field) {
            setDetailSort(order === 'desc' ? `-${field}` : field);
        } else {
            setDetailSort('-earned_date');
        }
        resetToFirstPage();
    };

    const chartData = useMemo(
        () =>
            (summary?.monthly_trend ?? []).map((point) => ({
                label: formatMonthLabel(point.month),
                paid: fromCents(point.paid),
            })),
        [summary],
    );

    const typeTotal = useMemo(
        () => (summary?.type_breakdown ?? []).reduce((acc, row) => acc + row.amount, 0),
        [summary],
    );

    const recipientColumns: GridColDef<CompPlanRecipient>[] = useMemo(() => {
        const money = (value: number | null) => (value == null ? '' : formatCents(value));
        return [
            { field: 'organization_name', headerName: 'Recipient', minWidth: 240, flex: 1 },
            {
                field: 'in_view',
                headerName: 'In View',
                width: 130,
                type: 'number',
                valueFormatter: money,
            },
            {
                field: 'paid_ytd',
                headerName: 'Paid YTD',
                width: 130,
                type: 'number',
                valueFormatter: money,
            },
            {
                field: 'paid_trailing_12',
                headerName: 'Trailing 12 Mo',
                width: 150,
                type: 'number',
                valueFormatter: money,
            },
            {
                field: 'paid_all_time',
                headerName: 'All Time',
                width: 130,
                type: 'number',
                valueFormatter: money,
            },
            { field: 'payment_count', headerName: 'Payments', width: 110, type: 'number' },
            { field: 'last_paid', headerName: 'Last Paid', width: 130 },
        ];
    }, []);

    const detailColumns: GridColDef<Commission>[] = useMemo(
        () => [
            {
                field: 'earned_date',
                headerName: 'Earned Date',
                width: 130,
                valueFormatter: (value) => toIsoDate(value as string),
            },
            {
                field: 'partner.organization',
                headerName: 'Recipient',
                minWidth: 240,
                flex: 1,
                sortable: false,
                valueGetter: (_value, row) => row.partner?.organization?.name ?? 'Unassigned',
            },
            { field: 'type', headerName: 'Type', minWidth: 260, flex: 1, sortable: false },
            {
                field: 'amount',
                headerName: 'Amount',
                width: 140,
                type: 'number',
                valueFormatter: (value) => formatCents(value as number),
            },
            {
                field: 'is_paid',
                headerName: 'Status',
                width: 110,
                sortable: false,
                renderCell: (params) =>
                    params.row.is_paid ? (
                        <span style={{ color: theme.palette.success.main }}>PAID</span>
                    ) : (
                        <span style={{ color: theme.palette.warning.main }}>UNPAID</span>
                    ),
            },
            {
                field: 'payout',
                headerName: 'Stripe',
                width: 100,
                sortable: false,
                renderCell: (params) =>
                    params.row.payout?.transaction_id ? (
                        <Tooltip title="View payout in Stripe">
                            <Button
                                variant="outlined"
                                size="small"
                                href={`https://dashboard.stripe.com/transfers/${params.row.payout.transaction_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img src={StripeLogo} alt="stripe" height={14} width={14} />
                            </Button>
                        </Tooltip>
                    ) : null,
            },
        ],
        [theme],
    );

    const kpis = summary?.kpis;

    return (
        <>
            <Box sx={{ m: '1.5rem 2rem' }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Comp Plan Payouts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Who has been paid, how much, and when — with year-to-date totals per recipient,
                    monthly trends, and a full drill-down. Figures are aggregated on the server.
                    YTD, Trailing 12 Mo, and All Time always reflect paid payouts.
                </Typography>

                {(loadingReport || loadingDetail) && <LinearProgress sx={{ mb: 2 }} />}

                {/* Headline KPIs */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <SummaryCard
                        label="Paid YTD"
                        value={formatCents(kpis?.paid_ytd)}
                        sub={`Calendar ${ytdStartKey.slice(0, 4)}`}
                        color={theme.palette.success.main}
                    />
                    <SummaryCard
                        label="Paid This Month"
                        value={formatCents(kpis?.paid_this_month)}
                        sub={formatMonthLabel(currentMonthKey)}
                        color={theme.palette.success.main}
                    />
                    <SummaryCard label="In View" value={formatCents(kpis?.in_view_total)} />
                    <SummaryCard
                        label="Outstanding (Unpaid)"
                        value={formatCents(kpis?.in_view_unpaid)}
                        color={theme.palette.warning.main}
                    />
                    <SummaryCard
                        label="Recipients (In View)"
                        value={String(kpis?.recipient_count ?? 0)}
                    />
                </Box>

                {/* Filters */}
                <ResponsiveForm>
                    <ResponsiveFormItem>
                        <FormControl fullWidth size="small">
                            <InputLabel id="comp-plan-preset">Period</InputLabel>
                            <Select
                                labelId="comp-plan-preset"
                                label="Period"
                                value={datePreset}
                                onChange={(event: SelectChangeEvent<DatePreset>) =>
                                    applyPreset(event.target.value as DatePreset)
                                }
                            >
                                <MenuItem value="all">All time</MenuItem>
                                <MenuItem value="ytd">Year to date</MenuItem>
                                <MenuItem value="last_year">Last year</MenuItem>
                                <MenuItem value="trailing_12">Trailing 12 months</MenuItem>
                            </Select>
                        </FormControl>
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            label="From month"
                            type="month"
                            size="small"
                            fullWidth
                            value={startMonth}
                            onChange={(e) => {
                                setStartMonth(e.target.value);
                                setDatePreset('all');
                                resetToFirstPage();
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            label="To month"
                            type="month"
                            size="small"
                            fullWidth
                            value={endMonth}
                            onChange={(e) => {
                                setEndMonth(e.target.value);
                                setDatePreset('all');
                                resetToFirstPage();
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <FormControl fullWidth size="small">
                            <InputLabel id="comp-plan-paid-filter">Payout Status</InputLabel>
                            <Select
                                labelId="comp-plan-paid-filter"
                                label="Payout Status"
                                value={paidFilter}
                                onChange={(event: SelectChangeEvent<PaidFilter>) => {
                                    setPaidFilter(event.target.value as PaidFilter);
                                    resetToFirstPage();
                                }}
                            >
                                <MenuItem value="paid">Paid only</MenuItem>
                                <MenuItem value="unpaid">Unpaid only</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                            </Select>
                        </FormControl>
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Autocomplete
                            value={(recipient as Organization | null) ?? null}
                            onChange={(event, newValue) => {
                                setRecipient(
                                    newValue ? { uuid: newValue.uuid, name: newValue.name } : null,
                                );
                                resetToFirstPage();
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchOrganizations(newInputValue);
                                }
                            }}
                            getOptionKey={(option) => option.uuid}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            filterOptions={(options) => options}
                            size="small"
                            fullWidth
                            options={organizations}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => <TextField {...params} label="Recipient" />}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => setRefreshKey((key) => key + 1)}
                            fullWidth={isNarrow}
                        >
                            Refresh
                        </Button>
                    </ResponsiveFormItem>
                </ResponsiveForm>

                {/* Monthly trend */}
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1.5 }}>
                    Monthly Paid Payouts
                </Typography>
                <Card variant="outlined">
                    <CardContent>
                        {chartData.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No payouts in the selected view.
                            </Typography>
                        ) : (
                            <Box sx={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) =>
                                                currencyCompact.format(value as number)
                                            }
                                        />
                                        <RechartsTooltip
                                            formatter={(value) => currency.format(value as number)}
                                        />
                                        <Bar
                                            dataKey="paid"
                                            name="Paid Payouts"
                                            fill={theme.palette.primary.main}
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Payouts by type */}
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1.5 }}>
                    Payouts by Type
                </Typography>
                <Card variant="outlined">
                    <CardContent sx={{ p: 0 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Commission Type</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="right">% of Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(summary?.type_breakdown ?? []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                No payouts in the selected view.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (summary?.type_breakdown ?? []).map((row) => (
                                        <TableRow key={row.type}>
                                            <TableCell>{row.type}</TableCell>
                                            <TableCell align="right">
                                                {formatCents(row.amount)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {typeTotal > 0
                                                    ? ((row.amount / typeTotal) * 100).toFixed(1)
                                                    : '0.0'}
                                                %
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Recipient leaderboard */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 4,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        By Recipient
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={exportRecipientsCsv}
                        disabled={recipients.length === 0}
                    >
                        CSV
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Click a recipient to filter the whole report to them.
                </Typography>
                <Box width="100%" sx={{ mt: 1 }} className="dataGridContainer">
                    <DataGridPremium
                        rows={recipients}
                        columns={recipientColumns}
                        loading={loadingReport}
                        getRowId={(row) => row.organization_uuid}
                        onRowClick={(params) => {
                            setRecipient({
                                uuid: params.row.organization_uuid,
                                name: params.row.organization_name,
                            });
                            resetToFirstPage();
                        }}
                        disableRowSelectionOnClick
                        pagination
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            sorting: { sortModel: [{ field: 'paid_ytd', sort: 'desc' }] },
                        }}
                        style={{ minHeight: '400px', maxHeight: '560px', cursor: 'pointer' }}
                        sx={{
                            '& .MuiDataGrid-row': { cursor: 'pointer' },
                            '& .MuiDataGrid-cell:focus': { outline: 'none' },
                            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        }}
                    />
                </Box>

                {/* Drill-down detail */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 4,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Payout Detail{recipient ? ` — ${recipient.name}` : ''}
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={isExporting || totalCommissions === 0}
                    >
                        {isExporting ? 'Exporting…' : 'CSV'}
                    </Button>
                </Box>
                <Divider sx={{ mt: 1 }} />
                <Box width="100%" sx={{ mt: 2 }} className="dataGridContainer">
                    <DataGridPremium
                        rows={commissions}
                        columns={detailColumns}
                        loading={loadingDetail}
                        getRowId={(row) => row.uuid}
                        sortingMode="server"
                        onSortModelChange={handleDetailSort}
                        paginationMode="server"
                        rowCount={totalCommissions}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        pageSizeOptions={[25, 50, 100]}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        style={{ minHeight: '500px', maxHeight: '680px', marginBottom: '50px' }}
                        sx={{
                            '& .MuiDataGrid-cell:focus': { outline: 'none' },
                            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        }}
                    />
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ open: false, message: '' })}
                message={snackbar.message}
            />
        </>
    );
};

export default CompPlanReport;
